// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CachedWorkflowStageListProperty } from '@/audit/api/types';
import { Box, Typography } from '@mui/material';

interface Props {
  /**
   * The workflow stage list property to render.
   */
  property: CachedWorkflowStageListProperty;
}

type WorkflowStageItem = NonNullable<
  CachedWorkflowStageListProperty['newValue']
>[0];

/**
 * Compares two stage arrays and returns whether they are different
 */
function stageListsAreDifferent(
  oldStages: WorkflowStageItem[] | null | undefined,
  newStages: WorkflowStageItem[] | null
): boolean {
  if (!oldStages && !newStages) {
    return false;
  }

  if (!oldStages || !newStages) {
    return true;
  }

  if (oldStages.length !== newStages.length) {
    return true;
  }

  // Sort both arrays by orderIndex to ensure consistent comparison
  const sortedOld = [...oldStages].sort((a, b) => a.orderIndex - b.orderIndex);
  const sortedNew = [...newStages].sort((a, b) => a.orderIndex - b.orderIndex);

  // Check if any stage properties changed
  for (let i = 0; i < sortedOld.length; i++) {
    const oldStage = sortedOld[i];
    const newStage = sortedNew[i];

    if (
      oldStage.id !== newStage.id ||
      oldStage.name !== newStage.name ||
      oldStage.orderIndex !== newStage.orderIndex ||
      oldStage.key !== newStage.key ||
      oldStage.description !== newStage.description
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Formats a stage list for display
 */
function formatStageList(stages: WorkflowStageItem[]): string {
  return stages
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((stage) => `   ${stage.orderIndex}) ${stage.name}`)
    .join('\n');
}

/**
 * A component that renders workflow stage list changes, showing the full stage comparison.
 */
export function AuditWorkflowStageListLabel(props: Props) {
  // For CREATE events, show stages if there's a newValue
  if (!props.property.oldValue && props.property.newValue) {
    const currentStages = formatStageList(props.property.newValue);
    return (
      <Box>
        <Typography
          variant="caption"
          component="div"
          sx={{ fontWeight: 'bold' }}
        >
          Stages:
        </Typography>
        <Box sx={{ ml: 1 }}>
          <Typography
            variant="caption"
            component="pre"
            sx={{
              fontFamily: 'inherit',
              whiteSpace: 'pre-wrap',
            }}
          >
            {currentStages}
          </Typography>
        </Box>
      </Box>
    );
  }

  // For UPDATE events, show comparison if there are changes
  const hasChanges = stageListsAreDifferent(
    props.property.oldValue,
    props.property.newValue
  );

  if (!hasChanges || !props.property.oldValue || !props.property.newValue) {
    return null;
  }

  const previousStages = formatStageList(props.property.oldValue);
  const currentStages = formatStageList(props.property.newValue);

  return (
    <Box>
      <Typography variant="caption" component="div" sx={{ fontWeight: 'bold' }}>
        Stages:
      </Typography>
      <Box sx={{ ml: 1 }}>
        <Typography
          variant="caption"
          component="div"
          sx={{ fontWeight: 'bold' }}
        >
          Previous:
        </Typography>
        <Typography
          variant="caption"
          component="pre"
          sx={{
            fontFamily: 'inherit',
            whiteSpace: 'pre-wrap',
          }}
        >
          {previousStages}
        </Typography>

        <Typography
          variant="caption"
          component="div"
          sx={{ fontWeight: 'bold' }}
        >
          Current:
        </Typography>
        <Typography
          variant="caption"
          component="pre"
          sx={{
            fontFamily: 'inherit',
            whiteSpace: 'pre-wrap',
          }}
        >
          {currentStages}
        </Typography>
      </Box>
    </Box>
  );
}
